import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import {
    Add,
    ArrowDownward,
    ArrowDropDown,
    ArrowUpward,
    Assignment,
    Clear,
    Link as LinkIcon,
    MoreVert,
    Restaurant,
    Search,
    SwapVert,
} from "@material-ui/icons"
import { Autocomplete } from "@material-ui/lab"
import React, { useMemo, useState } from "react"
import { salvarFichaTecnica, vinculosFichaTecnica } from "../../../services/fichaTecnica"
import {
    atualizarControlarEstoque,
    atualizarEstoqueNegativo,
    buscarHistorico,
    deletarInusmo,
} from "../../../services/insumos"
import useAppStore from "../../../stores/AppStore"
import useLoadingStore from "../../../stores/LoadingStore"
import { useSnackbar } from "../../../utils/notificacoes"
import { formatQuantidadeParaView, formatValorUnitarioParaView } from "../utils/formatacao"
import { useCardapioContext } from "./CardapioContext"
import DialogHistorico from "./DialogHistorico"
import DialogInsumo from "./DialogInsumo"

const unidades = {
    U: "Unidade",
    G: "Grama",
    KG: "Quilograma",
    L: "Litro",
    ML: "Mililitro",
}

const useStyles = makeStyles(theme => ({
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            marginTop: "4rem",
            flexDirection: "column",
            alignItems: "flex-start",
        },
    },

    tableContainer: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
    },

    tableScroll: {
        overflowX: "auto",
    },

    tableHeader: {
        backgroundColor: "#f8f9fa",
        "& .MuiTableCell-head": {
            fontWeight: 600,
            color: "#2c3e50",
            borderBottom: "2px solid #e9ecef",
        },
    },

    tableRow: {
        transition: "all 0.2s ease",
        "&:hover": {
            backgroundColor: "#f8f9fa",
        },
        "& .MuiTableCell-root": {
            borderBottom: "1px solid #f1f3f4",
            padding: theme.spacing(2),
        },
    },

    searchContainer: {
        marginBottom: theme.spacing(3),
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "center",
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
            gap: theme.spacing(1.5),
        },
    },

    searchField: {
        backgroundColor: "white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        flex: 1,
        "& .MuiOutlinedInput-root": {
            borderRadius: 25,
            border: "none !important",
            outline: "none !important",
            "& fieldset": {
                border: "none !important",
                outline: "none !important",
            },
            "&:hover fieldset": {
                border: "none !important",
                outline: "none !important",
            },
            "&.Mui-focused fieldset": {
                border: "none !important",
                outline: "none !important",
            },
        },
        "& .MuiInputBase-root": {
            border: "none !important",
            outline: "none !important",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none !important",
            outline: "none !important",
        },
    },

    actionsRow: {
        display: "flex",
        gap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            flexDirection: "column",
            gap: theme.spacing(1.5),
        },
    },

    addButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        padding: theme.spacing(1.5, 3),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(var(--cor-botao-salvar-rgb), 0.3)",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
        "&:hover": {
            backgroundColor: "var(--cor-botao-salvar-hover)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(var(--cor-botao-salvar-rgb), 0.4)",
        },
    },

    historyButton: {
        borderRadius: 25,
        backgroundColor: "#2196f3",
        color: "white",
        padding: theme.spacing(1.5, 3),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(33, 150, 243, 0.3)",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
        "&:hover": {
            backgroundColor: "#1976d2",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(33, 150, 243, 0.4)",
        },
    },

    edicaoEmMassaButton: {
        borderRadius: 25,
        backgroundColor: "#8e44ad",
        color: "white",
        padding: theme.spacing(1.5, 3),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(142, 68, 173, 0.3)",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
        },
        "&:hover": {
            backgroundColor: "#7d3c98",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(142, 68, 173, 0.4)",
        },
        "&.Mui-disabled": {
            opacity: 0.5,
            color: "white",
        },
    },

    statusAcaoEmMassa: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        marginRight: theme.spacing(1.5),
        flexShrink: 0,
    },

    emptyState: {
        textAlign: "center",
        padding: theme.spacing(8),
        color: "#95a5a6",
    },

    paginationContainer: {
        backgroundColor: "white",
        borderTop: "1px solid #f1f3f4",
        padding: theme.spacing(1),
    },

    mobileCard: {
        borderRadius: 16,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        padding: theme.spacing(2),
        backgroundColor: "white",
    },
    mobileCardRow: {
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    mobileLabel: { color: "#95a5a6" },

    linkButton: {
        borderRadius: 12,
        color: "#4caf50",
        border: "1px solid #4caf50",
        "&:hover": {
            backgroundColor: "#f1f8e9",
        },
    },
}))

export default function Insumos() {
    const { insumos, setInsumos, cardapio } = useCardapioContext()
    const setLoading = useLoadingStore(s => s.setLoading)
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg, tipo) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const configs = useAppStore(s => s.configuracoes)
    const idEstabelecimento = configs?.user?.parametros?.estabelecimento?.id
    const autorizacao = localStorage.getItem("chaveSistema")

    const [insumosSelecionados, setInsumosSelecionados] = useState([])

    const itensSelecionados = (insumos || []).filter(i => insumosSelecionados.includes(i.id))
    const totalSelecionados = itensSelecionados.length
    const coresAcaoEmMassa = {
        todos: "#27ae60",
        nenhum: "#e74c3c",
        alguns: "#f39c12",
    }

    const acoesEmMassa = [
        {
            campo: "estoque_negativo",
            rotulo: "Estoque negativo",
            servico: atualizarEstoqueNegativo,
        },
        {
            campo: "controlar_estoque",
            rotulo: "Controlar estoque",
            servico: atualizarControlarEstoque,
        },
    ]

    const statusAcaoEmMassa = campo => {
        const qtdAtivos = itensSelecionados.filter(i => i[campo]).length
        if (totalSelecionados === 0 || qtdAtivos === 0) return "nenhum"
        return qtdAtivos === totalSelecionados ? "todos" : "alguns"
    }

    const handleAcaoEmMassa = async ({ campo, rotulo, servico }) => {
        setAnchorEdicaoEmMassa(null)
        // ativo em todos os selecionados -> desativa; desativado ou parcial -> ativa
        const novoValor = statusAcaoEmMassa(campo) !== "todos"
        const insumoIds = itensSelecionados.map(i => i.id)
        try {
            setLoading(true)
            const response = await servico(idEstabelecimento, insumoIds, novoValor, autorizacao)
            if (response?.retornoErro === 1) {
                alertStart(response.mensagem, "error")
                return
            }
            setInsumos(prev =>
                prev?.map(i => (insumoIds.includes(i.id) ? { ...i, [campo]: novoValor } : i))
            )
            setInsumosSelecionados([])
            alertStart(
                `${rotulo} ${novoValor ? "ativado" : "desativado"} nos itens selecionados`,
                "success"
            )
        } finally {
            setLoading(false)
        }
    }

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [anchorEl, setAnchorEl] = useState(null)
    const [anchorEdicaoEmMassa, setAnchorEdicaoEmMassa] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [openDialog, setOpenDialog] = useState(false)
    const [insumo, setInsumo] = useState(null)
    const [openDialogExcluir, setOpenDialogExcluir] = useState(false)
    const [idInsumoExcluir, setIdInsumoExcluir] = useState(null)
    const [ordem, setOrdem] = useState(null)
    const [busca, setBusca] = useState("")
    const [openHistorico, setOpenHistorico] = useState(false)
    const [historico, setHistorico] = useState(null)
    const [openDialogVinculacao, setOpenDialogVinculacao] = useState(false)
    const [insumoParaVincular, setInsumoParaVincular] = useState(null)
    const [produtosSelecionados, setProdutosSelecionados] = useState([])
    const [buscaProdutos, setBuscaProdutos] = useState("")
    const [tabValue, setTabValue] = useState(0)
    const [opcoesSelecionadas, setOpcoesSelecionadas] = useState([])
    const [buscaOpcoes, setBuscaOpcoes] = useState("")
    const [filtroCategorias, setFiltroCategorias] = React.useState([])

    const toggleOrdem = () =>
        setOrdem(prev => (prev === null ? "asc" : prev === "asc" ? "desc" : null))

    const handleBusca = event => {
        setBusca(event.target.value)
        setPage(0)
    }

    function createData(
        nome,
        valorUnitario,
        estoqueAtual,
        estoqueMinimo,
        unidadeDeMedida,
        acoes,
        quantidade_minima,
        controlarEstoque,
        estoqueAtualRaw,
        id
    ) {
        return {
            id,
            nome,
            valorUnitario,
            estoqueAtual,
            estoqueMinimo,
            unidadeDeMedida,
            acoes,
            quantidade_minima,
            controlarEstoque,
            estoqueAtualRaw,
        }
    }

    const sortedInsumos = useMemo(() => {
        if (ordem === "asc") {
            return [...(insumos || [])].sort(
                (a, b) => (a.estoque?.quantidade ?? 0) - (b.estoque?.quantidade ?? 0)
            )
        }
        if (ordem === "desc") {
            return [...(insumos || [])].sort(
                (a, b) => (b.estoque?.quantidade ?? 0) - (a.estoque?.quantidade ?? 0)
            )
        }
        return insumos
    }, [insumos, ordem])

    const filteredInsumos = useMemo(() => {
        if (!sortedInsumos) return []
        return sortedInsumos?.filter(item => item.nome.toLowerCase().includes(busca.toLowerCase()))
    }, [sortedInsumos, busca])

    const todosFiltradosSelecionados =
        filteredInsumos.length > 0 && filteredInsumos.every(i => insumosSelecionados.includes(i.id))
    const algunsFiltradosSelecionados =
        !todosFiltradosSelecionados && filteredInsumos.some(i => insumosSelecionados.includes(i.id))

    const toggleSelecionarTodos = () => {
        const idsFiltrados = filteredInsumos.map(i => i.id)
        setInsumosSelecionados(prev =>
            todosFiltradosSelecionados
                ? prev.filter(id => !idsFiltrados.includes(id))
                : Array.from(new Set([...prev, ...idsFiltrados]))
        )
    }

    const toggleSelecionado = id =>
        setInsumosSelecionados(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )

    const handleChangePage = (_, newPage) => setPage(newPage)

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(+event.target.value)
        setPage(0)
    }

    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget)
        setSelectedRow(row)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        setSelectedRow(null)
    }

    const editar = row => {
        handleMenuClose()
        setInsumo(row)
        setOpenDialog(true)
    }

    const excluir = row => {
        setOpenDialogExcluir(true)
        setIdInsumoExcluir(row.id)
        handleMenuClose()
    }

    const confirmarExclusao = async () => {
        try {
            setLoading(true)
            const response = await deletarInusmo(idEstabelecimento, idInsumoExcluir, autorizacao)

            if (response.retornoErro === 1) {
                return alertStart(response.mensagem, "error")
            } else {
                setInsumos(prev => prev?.filter(insumo => insumo.id !== idInsumoExcluir))
                setInsumosSelecionados(prev => prev.filter(id => id !== idInsumoExcluir))

                setPage(prevPage => {
                    const totalItens = (insumos?.length || 0) - 1
                    const ultimaPagina = Math.max(0, Math.ceil(totalItens / rowsPerPage) - 1)
                    return prevPage > ultimaPagina ? ultimaPagina : prevPage
                })

                alertStart("Insumo deletado com sucesso", "success")
            }
        } catch (error) {
            console.error("Erro excluir => ", error)
            alertStart("Erro ao excluir insumo " + error, "error")
        } finally {
            setOpenDialogExcluir(false)
            setLoading(false)
        }
    }

    const historicoBuscar = async (inicio, fim) => {
        try {
            setLoading(true)
            const response = await buscarHistorico(idEstabelecimento, inicio, fim, autorizacao)
            if (response.retornoErro) {
                return alertStart(response.mensagem, "error")
            }
            setHistorico(response)
        } catch (error) {
            console.error("Erro histórico => ", error)
            alertStart("Erro ao buscar histórico", "error")
        } finally {
            setLoading(false)
        }
    }

    const abrirDialogVinculacao = insumo => {
        setInsumoParaVincular(insumo)
        setProdutosSelecionados([])
        setOpcoesSelecionadas([])
        setBuscaProdutos("")
        setBuscaOpcoes("")
        setTabValue(0)
        setOpenDialogVinculacao(true)
    }

    const criarFichaTecnica = async () => {
        const totalSelecionados = produtosSelecionados.length + opcoesSelecionadas.length

        if (!insumoParaVincular || totalSelecionados === 0) {
            alertStart("Selecione pelo menos um produto ou opção", "error")
            return
        } else if (insumoParaVincular.unidade_medida !== "U") {
            alertStart(
                'Apenas insumos medidos em "Unidade" são permitidos em vínculos diretos a um produto',
                "error"
            )
            return
        }

        try {
            setLoading(true)

            for (const produto of produtosSelecionados) {
                const fichaTecnica = {
                    nome: `${insumoParaVincular.nome} - ${produto.nome}`,
                    descricao: `Ficha técnica automática para ${produto.nome} usando ${insumoParaVincular.nome}`,
                    insumos: [
                        {
                            id: insumoParaVincular.id,
                            quantidade: 1,
                        },
                    ],
                }

                const responseFTI = await salvarFichaTecnica(
                    idEstabelecimento,
                    fichaTecnica,
                    autorizacao
                )
                if (responseFTI.retornoErro === 1) return alertStart(responseFTI.mensagem, "error")

                const dataEnviar = {
                    fichaTecnicaId: responseFTI.id,
                    opcaoIds: [],
                    produtoIds: [produto.id],
                }

                const responseVinculo = await vinculosFichaTecnica(
                    idEstabelecimento,
                    dataEnviar,
                    autorizacao
                )
                if (responseVinculo.retornoErro === 1)
                    return alertStart(responseFTI.mensagem, "error")
            }

            for (const opcao of opcoesSelecionadas) {
                const fichaTecnica = {
                    nome: `${insumoParaVincular.nome} - ${opcao.nome}`,
                    descricao: `Ficha técnica automática para opção ${opcao.nome} (${opcao.produto}) usando ${insumoParaVincular.nome}`,
                    insumos: [
                        {
                            id: insumoParaVincular.id,
                            quantidade: 1,
                        },
                    ],
                }

                const responseFTI = await salvarFichaTecnica(
                    idEstabelecimento,
                    fichaTecnica,
                    autorizacao
                )
                if (responseFTI.retornoErro === 1) return alertStart(responseFTI.mensagem, "error")

                const dataEnviar = {
                    fichaTecnicaId: responseFTI.id,
                    opcaoIds: [opcao.id],
                    produtoIds: [],
                }

                const responseVinculo = await vinculosFichaTecnica(
                    idEstabelecimento,
                    dataEnviar,
                    autorizacao
                )
                if (responseVinculo.retornoErro === 1)
                    return alertStart(responseFTI.mensagem, "error")
            }

            alertStart(`Ficha técnica criada com sucesso`, "success")
            setOpenDialogVinculacao(false)
        } catch (error) {
            console.error("Erro ao criar fichas técnicas:", error)
            alertStart("Erro ao criar fichas técnicas", "error")
        } finally {
            setLoading(false)
        }
    }

    const produtos =
        cardapio?.categorias?.reduce((acc, categoria) => {
            if (categoria.produtos && Array.isArray(categoria.produtos)) {
                const produtosDaCategoria = categoria.produtos.map(produto => ({
                    id: produto.id,
                    nome: produto.nome,
                    categoria: categoria.nome,
                    codigo: produto.codigo,
                    descricao: produto.descricao,
                    ativo: produto.ativo,
                }))
                return [...acc, ...produtosDaCategoria]
            }
            return acc
        }, []) || []

    const opcoes =
        cardapio?.categorias?.reduce((acc, categoria) => {
            if (categoria.produtos && Array.isArray(categoria.produtos)) {
                const opcoesDaCategoria = categoria.produtos.reduce((accProdutos, produto) => {
                    if (produto.opcoes && Array.isArray(produto.opcoes)) {
                        const opcoesDoProduto = produto.opcoes.reduce((accOpcoes, opcaoGrupo) => {
                            if (opcaoGrupo.opcoes && Array.isArray(opcaoGrupo.opcoes)) {
                                const opcoesDoGrupo = opcaoGrupo.opcoes.map(opcao => ({
                                    id: opcao.id,
                                    nome: opcao.nome,
                                    codigo: opcao.codigo,
                                    descricao: opcao.descricao,
                                    ativo: opcao.ativo,
                                    produto: produto.nome,
                                    produtoId: produto.id,
                                    categoria: categoria.nome,
                                    adicional: opcao.adicional,
                                    considerarMaiorValor: opcao.considerarMaiorValor,
                                    considerarMedia: opcao.considerarMedia,
                                }))
                                return [...accOpcoes, ...opcoesDoGrupo]
                            }
                            return accOpcoes
                        }, [])
                        return [...accProdutos, ...opcoesDoProduto]
                    }
                    return accProdutos
                }, [])
                return [...acc, ...opcoesDaCategoria]
            }
            return acc
        }, []) || []

    const produtosFiltrados = produtos.filter(produto => {
        const termoBusca = buscaProdutos.toLowerCase()
        const matchBusca =
            produto.nome?.toLowerCase().includes(termoBusca) ||
            produto.categoria?.toLowerCase().includes(termoBusca) ||
            produto.codigo?.toLowerCase().includes(termoBusca)

        const matchCategoria =
            filtroCategorias.length === 0 || filtroCategorias.includes(produto.categoria)

        return matchBusca && matchCategoria
    })

    const opcoesFiltradas = opcoes.filter(opcao => {
        const termoBusca = buscaOpcoes.toLowerCase()
        const matchBusca =
            opcao.nome?.toLowerCase().includes(termoBusca) ||
            opcao.produto?.toLowerCase().includes(termoBusca) ||
            opcao.categoria?.toLowerCase().includes(termoBusca) ||
            opcao.codigo?.toLowerCase().includes(termoBusca) ||
            opcao.descricao?.toLowerCase().includes(termoBusca)

        const matchCategoria =
            filtroCategorias.length === 0 || filtroCategorias.includes(opcao.categoria)

        return matchBusca && matchCategoria
    })

    const rows =
        filteredInsumos?.length >= 1
            ? filteredInsumos?.map(item => {
                  const unidadeMostrar = unidades[item.unidade_medida] || item.unidade_medida

                  return createData(
                      item.nome,
                      item.valor_unitario ? item.valor_unitario : null,
                      item.estoque ? formatQuantidadeParaView(item.estoque?.quantidade) : null,
                      item.estoque
                          ? formatQuantidadeParaView(item.estoque?.quantidade_minima) +
                                " " +
                                item.unidade_medida
                          : null,
                      unidadeMostrar,
                      <IconButton size="small" onClick={event => handleMenuOpen(event, item)}>
                          <MoreVert />
                      </IconButton>,
                      item.estoque?.quantidade_minima,
                      item.controlar_estoque,
                      item.estoque?.quantidade,
                      item.id
                  )
              })
            : []

    const dataHoje = new Date()
    const pad = n => String(n).padStart(2, "0")
    const yyyy = dataHoje.getFullYear()
    const mm = pad(dataHoje.getMonth() + 1)
    const dd = pad(dataHoje.getDate())
    const yyyyMMdd = `${yyyy}-${mm}-${dd}`

    const categoriasDoEstabelecimento = useMemo(() => {
        const nomes = (cardapio?.categorias ?? []).map(c => c?.nome).filter(Boolean)
        return Array.from(new Set(nomes)).sort()
    }, [cardapio])

    const paginatedRows = rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || []

    return (
        <Box style={{ marginBottom: "1.5em" }}>
            <Box className={classes.header}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h4" style={{ fontWeight: 600, color: "#2c3e50" }}>
                        Estoque
                    </Typography>
                </Box>
                <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                    {insumos?.length || 0} Itens cadastrados
                </Typography>
            </Box>

            <Box className={classes.searchContainer}>
                <TextField
                    className={classes.searchField}
                    placeholder="Buscar insumos por nome..."
                    variant="outlined"
                    size="small"
                    value={busca}
                    onChange={handleBusca}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search style={{ color: "#95a5a6" }} />
                            </InputAdornment>
                        ),
                        endAdornment: busca && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setBusca("")}>
                                    <Clear fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Box className={classes.actionsRow}>
                    <Button
                        className={classes.addButton}
                        startIcon={<Add />}
                        onClick={() => {
                            setInsumo(null)
                            setOpenDialog(true)
                        }}
                    >
                        Novo Produto
                    </Button>

                    <Button
                        className={classes.historyButton}
                        startIcon={<Assignment />}
                        onClick={() => {
                            historicoBuscar(yyyyMMdd, yyyyMMdd)
                            setOpenHistorico(true)
                        }}
                    >
                        Histórico
                    </Button>

                    <Button
                        className={classes.edicaoEmMassaButton}
                        disabled={totalSelecionados === 0}
                        onClick={e => setAnchorEdicaoEmMassa(e.currentTarget)}
                        endIcon={<ArrowDropDown />}
                    >
                        Edição em massa
                        {totalSelecionados > 0 ? ` (${totalSelecionados})` : ""}
                    </Button>

                    <Menu
                        anchorEl={anchorEdicaoEmMassa}
                        open={Boolean(anchorEdicaoEmMassa)}
                        onClose={() => setAnchorEdicaoEmMassa(null)}
                        getContentAnchorEl={null}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                        {acoesEmMassa.map(acao => {
                            const status = statusAcaoEmMassa(acao.campo)
                            return (
                                <MenuItem key={acao.campo} onClick={() => handleAcaoEmMassa(acao)}>
                                    <span
                                        className={classes.statusAcaoEmMassa}
                                        style={{ backgroundColor: coresAcaoEmMassa[status] }}
                                    />
                                    <Box>
                                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                                            {acao.rotulo}
                                        </Typography>
                                        <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                            {status === "todos" ? "Desativar" : "Ativar"}{" "}
                                            {totalSelecionados === 1
                                                ? "no item selecionado"
                                                : `nos ${totalSelecionados} itens selecionados`}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            )
                        })}
                        <Box px={2} pt={1} style={{ borderTop: "1px solid #f1f3f4" }}>
                            <Typography variant="caption" component="div">
                                <b style={{ color: coresAcaoEmMassa.todos }}>Verde:</b> ativo em
                                todos
                            </Typography>
                            <Typography variant="caption" component="div">
                                <b style={{ color: coresAcaoEmMassa.nenhum }}>Vermelho:</b>{" "}
                                desativado em todos
                            </Typography>
                            <Typography variant="caption" component="div">
                                <b style={{ color: coresAcaoEmMassa.alguns }}>Laranja:</b> ativo em
                                apenas alguns
                            </Typography>
                        </Box>
                    </Menu>
                </Box>
            </Box>

            {filteredInsumos.length > 0 ? (
                isMobile ? (
                    <Box display="grid" gridGap={16}>
                        <Box display="flex" alignItems="center">
                            <Checkbox
                                color="primary"
                                checked={todosFiltradosSelecionados}
                                indeterminate={algunsFiltradosSelecionados}
                                onChange={toggleSelecionarTodos}
                                inputProps={{ "aria-label": "Selecionar todos os insumos" }}
                            />
                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                Selecionar todos
                            </Typography>
                        </Box>
                        {paginatedRows.map((row, index) => {
                            const total =
                                row.estoqueAtualRaw != null && row.valorUnitario != null
                                    ? (
                                          Number(row.estoqueAtualRaw) * Number(row.valorUnitario)
                                      ).toLocaleString("pt-BR", {
                                          style: "currency",
                                          currency: "BRL",
                                      })
                                    : "-"

                            const corEstoque = row.controlarEstoque
                                ? parseFloat(row.estoqueAtualRaw) === 0
                                    ? "var(--cor-erro)"
                                    : parseFloat(row.estoqueAtualRaw) <
                                      Number(row.quantidade_minima)
                                    ? "#f57c00"
                                    : "#4caf50"
                                : "#7f8c8d"

                            const bgEstoque = row.controlarEstoque
                                ? parseFloat(row.estoqueAtualRaw) === 0
                                    ? "rgba(255,0,0,0.1)"
                                    : parseFloat(row.estoqueAtualRaw) <
                                      Number(row.quantidade_minima)
                                    ? "rgba(255, 193, 7, 0.1)"
                                    : "rgba(var(--cor-botao-salvar-rgb), 0.1)"
                                : "transparent"

                            return (
                                <Paper key={index} className={classes.mobileCard}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        gridGap={12}
                                    >
                                        <Checkbox
                                            color="primary"
                                            size="small"
                                            style={{ padding: 0, marginTop: 4 }}
                                            checked={insumosSelecionados.includes(row.id)}
                                            onChange={() => toggleSelecionado(row.id)}
                                            inputProps={{ "aria-label": `Selecionar ${row.nome}` }}
                                        />
                                        <Box minWidth={0} flex={1}>
                                            <Typography
                                                variant="subtitle1"
                                                style={{
                                                    fontWeight: 700,
                                                    color: "#2c3e50",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                                title={row.nome}
                                            >
                                                {row.nome}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                style={{ color: "#7f8c8d", marginTop: 4 }}
                                            >
                                                {row.unidadeDeMedida}
                                            </Typography>
                                        </Box>

                                        <Box display="flex" alignItems="center" gridGap={8}>
                                            {row.unidadeDeMedida === "Unidade" && (
                                                <IconButton
                                                    size="small"
                                                    className={classes.linkButton}
                                                    onClick={() => {
                                                        const insumoOriginal =
                                                            filteredInsumos?.slice(
                                                                page * rowsPerPage,
                                                                page * rowsPerPage + rowsPerPage
                                                            )[index]
                                                        abrirDialogVinculacao(insumoOriginal)
                                                    }}
                                                    title="Criar ficha técnica automática"
                                                >
                                                    <LinkIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            {row.acoes}
                                        </Box>
                                    </Box>

                                    <Box className={classes.mobileCardRow}>
                                        <Typography variant="body2" className={classes.mobileLabel}>
                                            Valor Unitário
                                        </Typography>
                                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                            {row.valorUnitario != null
                                                ? `R$ ${formatValorUnitarioParaView(
                                                      row.valorUnitario
                                                  )}`
                                                : "-"}
                                        </Typography>
                                    </Box>

                                    <Box className={classes.mobileCardRow}>
                                        <Typography variant="body2" className={classes.mobileLabel}>
                                            Estoque Atual
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            style={{
                                                fontWeight: 700,
                                                color: corEstoque,
                                                backgroundColor: bgEstoque,
                                                padding: "4px 10px",
                                                borderRadius: 10,
                                            }}
                                        >
                                            {row.controlarEstoque
                                                ? row.estoqueAtual
                                                : "Não controlado"}
                                        </Typography>
                                    </Box>

                                    <Box className={classes.mobileCardRow}>
                                        <Typography variant="body2" className={classes.mobileLabel}>
                                            Estoque Mínimo
                                        </Typography>
                                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                            {row.estoqueMinimo || "-"}
                                        </Typography>
                                    </Box>

                                    <Box className={classes.mobileCardRow}>
                                        <Typography variant="body2" className={classes.mobileLabel}>
                                            Total em estoque
                                        </Typography>
                                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                            {total}
                                        </Typography>
                                    </Box>
                                </Paper>
                            )
                        })}

                        <Paper className={classes.paginationContainer}>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={rows?.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={isMobile ? "Itens" : "Itens por página"}
                                style={{ minWidth: isMobile ? "90vw" : null }}
                            />
                        </Paper>
                    </Box>
                ) : (
                    <Paper className={classes.tableContainer}>
                        <TableContainer className={classes.tableScroll}>
                            <Table style={{ minWidth: 980 }}>
                                <TableHead className={classes.tableHeader}>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={todosFiltradosSelecionados}
                                                indeterminate={algunsFiltradosSelecionados}
                                                onChange={toggleSelecionarTodos}
                                                inputProps={{
                                                    "aria-label": "Selecionar todos os insumos",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell style={{ width: "20%" }}>Nome</TableCell>
                                        <TableCell style={{ width: "20%" }}>
                                            Valor Unitário
                                        </TableCell>
                                        <TableCell style={{ width: "15%" }}>
                                            <Box display="flex" alignItems="center">
                                                Estoque Atual
                                                <IconButton size="small" onClick={toggleOrdem}>
                                                    {ordem === "asc" ? (
                                                        <ArrowUpward fontSize="small" />
                                                    ) : ordem === "desc" ? (
                                                        <ArrowDownward fontSize="small" />
                                                    ) : (
                                                        <SwapVert fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell style={{ width: "15%" }}>
                                            Estoque Mínimo
                                        </TableCell>
                                        <TableCell style={{ width: "10%" }}>Unidade</TableCell>
                                        <TableCell style={{ width: "20%" }}>
                                            Valor Total em Estoque
                                        </TableCell>
                                        <TableCell style={{ width: "10%" }}>Vincular</TableCell>
                                        <TableCell style={{ width: "8%" }} align="center">
                                            Ações
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginatedRows.map((row, index) => (
                                        <TableRow
                                            key={index}
                                            className={classes.tableRow}
                                            selected={insumosSelecionados.includes(row.id)}
                                        >
                                            <TableCell padding="checkbox" style={{ padding: 4 }}>
                                                <Checkbox
                                                    color="primary"
                                                    checked={insumosSelecionados.includes(row.id)}
                                                    onChange={() => toggleSelecionado(row.id)}
                                                    inputProps={{
                                                        "aria-label": `Selecionar ${row.nome}`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="subtitle1"
                                                    style={{
                                                        fontWeight: 600,
                                                        color: "#2c3e50",
                                                        maxWidth: "150px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        cursor: "default",
                                                    }}
                                                    title={row.nome}
                                                >
                                                    {row.nome}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d" }}
                                                >
                                                    R${" "}
                                                    {row.valorUnitario != null
                                                        ? formatValorUnitarioParaView(
                                                              row.valorUnitario
                                                          )
                                                        : "-"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                style={{
                                                    backgroundColor: row.controlarEstoque
                                                        ? parseFloat(row.estoqueAtualRaw) === 0
                                                            ? "rgba(255,0,0,0.1)"
                                                            : parseFloat(row.estoqueAtualRaw) <
                                                              Number(row.quantidade_minima)
                                                            ? "rgba(255, 193, 7, 0.1)"
                                                            : "rgba(var(--cor-botao-salvar-rgb), 0.1)"
                                                        : "transparent",
                                                    borderRadius: 8,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{
                                                        color: row.controlarEstoque
                                                            ? parseFloat(row.estoqueAtualRaw) === 0
                                                                ? "var(--cor-erro)"
                                                                : parseFloat(row.estoqueAtualRaw) <
                                                                  Number(row.quantidade_minima)
                                                                ? "#f57c00"
                                                                : "#4caf50"
                                                            : "#7f8c8d",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {row.controlarEstoque
                                                        ? row.estoqueAtual
                                                        : "Não controlado"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d" }}
                                                >
                                                    {row.estoqueMinimo || "-"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d" }}
                                                >
                                                    {row.unidadeDeMedida}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#7f8c8d" }}
                                                >
                                                    {row.estoqueAtualRaw != null &&
                                                    row.valorUnitario != null
                                                        ? (
                                                              Number(row.estoqueAtualRaw) *
                                                              Number(row.valorUnitario)
                                                          ).toLocaleString("pt-BR", {
                                                              style: "currency",
                                                              currency: "BRL",
                                                          })
                                                        : "-"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                {row.unidadeDeMedida === "Unidade" && (
                                                    <Box display="flex" alignItems="center">
                                                        <IconButton
                                                            size="small"
                                                            className={classes.linkButton}
                                                            onClick={() => {
                                                                const insumoOriginal =
                                                                    filteredInsumos?.slice(
                                                                        page * rowsPerPage,
                                                                        page * rowsPerPage +
                                                                            rowsPerPage
                                                                    )[index]
                                                                abrirDialogVinculacao(
                                                                    insumoOriginal
                                                                )
                                                            }}
                                                            title="Criar ficha técnica automática"
                                                        >
                                                            <LinkIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </TableCell>

                                            <TableCell align="center">{row.acoes}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box className={classes.paginationContainer}>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={rows?.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={isMobile ? "Itens" : "Itens por página"}
                                style={{ minWidth: isMobile ? "90vw" : null }}
                            />
                        </Box>
                    </Paper>
                )
            ) : (
                <Box className={classes.emptyState}>
                    <Typography variant="h6">Nenhum insumo encontrado</Typography>
                    <Typography variant="body2">
                        {busca ? "Tente ajustar sua busca" : "Comece criando seu primeiro insumo"}
                    </Typography>
                </Box>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                keepMounted
            >
                <MenuItem onClick={() => editar(selectedRow)}>Editar</MenuItem>
                <MenuItem onClick={() => excluir(selectedRow)}>Excluir</MenuItem>
            </Menu>

            <DialogInsumo
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                insumoModal={insumo}
                setInsumos={setInsumos}
                insumos={insumos}
            />

            <DialogHistorico
                open={openHistorico}
                setOpenHistorico={setOpenHistorico}
                historico={historico}
                historicoBuscar={historicoBuscar}
                dataHoje={yyyyMMdd}
            />

            <Dialog open={openDialogExcluir} onClose={() => setOpenDialogExcluir(false)}>
                <DialogTitle>Confirmar exclusão de insumo</DialogTitle>
                <DialogActions>
                    <Button onClick={() => setOpenDialogExcluir(false)}>Cancelar</Button>
                    <Button onClick={confirmarExclusao}>Confirmar</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDialogVinculacao}
                onClose={() => setOpenDialogVinculacao(false)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    style: { borderRadius: isMobile ? 0 : 20 },
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <Restaurant style={{ marginRight: 8, color: "#4caf50" }} />
                        Criar Ficha Técnica Automática
                    </Box>
                    {insumoParaVincular && (
                        <Typography variant="subtitle2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                            Insumo: {insumoParaVincular.nome}
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent style={{ padding: 24 }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, newValue) => setTabValue(newValue)}
                        indicatorColor="primary"
                        textColor="primary"
                        style={{ marginBottom: 16 }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : "off"}
                    >
                        <Tab label={`Produtos (${produtosFiltrados.length}/${produtos.length})`} />
                        <Tab label={`Opções (${opcoesFiltradas.length}/${opcoes.length})`} />
                    </Tabs>

                    {tabValue === 0 && (
                        <Typography
                            variant="subtitle1"
                            style={{ marginBottom: 16, color: "#34495e" }}
                        >
                            Selecione os produtos que receberão uma ficha técnica com este insumo:
                        </Typography>
                    )}

                    {tabValue === 1 && (
                        <Typography
                            variant="subtitle1"
                            style={{ marginBottom: 16, color: "#34495e" }}
                        >
                            Selecione as opções que receberão uma ficha técnica com este insumo:
                        </Typography>
                    )}

                    <Grid container spacing={1}>
                        {tabValue === 0 && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    placeholder="Buscar produtos por nome, categoria, código..."
                                    variant="outlined"
                                    size="small"
                                    value={buscaProdutos}
                                    onChange={e => setBuscaProdutos(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search style={{ color: "#95a5a6" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: buscaProdutos && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setBuscaProdutos("")}
                                                >
                                                    <Clear fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{
                                        marginBottom: 16,
                                        backgroundColor: "white",
                                        borderRadius: 15,
                                    }}
                                />
                            </Grid>
                        )}

                        {tabValue === 1 && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    placeholder="Buscar opções por nome, produto, categoria, código..."
                                    variant="outlined"
                                    size="small"
                                    value={buscaOpcoes}
                                    onChange={e => setBuscaOpcoes(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search style={{ color: "#95a5a6" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: buscaOpcoes && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setBuscaOpcoes("")}
                                                >
                                                    <Clear fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{
                                        marginBottom: 16,
                                        backgroundColor: "white",
                                        borderRadius: 15,
                                    }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <Box display="flex" gap={2} marginBottom={2}>
                                <Autocomplete
                                    multiple
                                    options={categoriasDoEstabelecimento}
                                    value={filtroCategorias}
                                    onChange={(_, value) => setFiltroCategorias(value)}
                                    filterSelectedOptions
                                    size="small"
                                    fullWidth
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="default"
                                                label={option}
                                                {...getTagProps({ index })}
                                                style={{ borderRadius: 14 }}
                                            />
                                        ))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            label="Filtrar por categorias"
                                            placeholder="Selecione categorias"
                                        />
                                    )}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {tabValue === 0 &&
                        (produtosFiltrados.length === 0 ? (
                            <Typography
                                variant="body2"
                                style={{
                                    color: "#95a5a6",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    padding: 32,
                                }}
                            >
                                {buscaProdutos
                                    ? "Nenhum produto encontrado com esse termo"
                                    : "Nenhum produto encontrado no cardápio"}
                            </Typography>
                        ) : (
                            <Box>
                                {produtosFiltrados.map(produto => {
                                    const isSelected = produtosSelecionados.some(
                                        p => p.id === produto.id
                                    )
                                    return (
                                        <Chip
                                            key={produto.id}
                                            label={`${produto.nome} ${
                                                produto.categoria ? `(${produto.categoria})` : ""
                                            }`}
                                            clickable
                                            color={isSelected ? "primary" : "default"}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setProdutosSelecionados(prev =>
                                                        prev.filter(p => p.id !== produto.id)
                                                    )
                                                } else {
                                                    setProdutosSelecionados(prev => [
                                                        ...prev,
                                                        produto,
                                                    ])
                                                }
                                            }}
                                            style={{
                                                margin: 4,
                                                borderRadius: 15,
                                                backgroundColor: isSelected ? "#4caf50" : "#f5f5f5",
                                                color: isSelected ? "white" : "#666",
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        ))}

                    {tabValue === 1 &&
                        (opcoesFiltradas.length === 0 ? (
                            <Typography
                                variant="body2"
                                style={{
                                    color: "#95a5a6",
                                    fontStyle: "italic",
                                    textAlign: "center",
                                    padding: 32,
                                }}
                            >
                                {buscaOpcoes
                                    ? "Nenhuma opção encontrada com esse termo"
                                    : "Nenhuma opção encontrada no cardápio"}
                            </Typography>
                        ) : (
                            <Box>
                                {opcoesFiltradas.map(opcao => {
                                    const isSelected = opcoesSelecionadas.some(
                                        o => o.id === opcao.id
                                    )
                                    return (
                                        <Chip
                                            key={opcao.id}
                                            label={`${opcao.nome} (${opcao.produto} - ${opcao.categoria})`}
                                            clickable
                                            color={isSelected ? "primary" : "default"}
                                            onClick={() => {
                                                if (isSelected) {
                                                    setOpcoesSelecionadas(prev =>
                                                        prev.filter(o => o.id !== opcao.id)
                                                    )
                                                } else {
                                                    setOpcoesSelecionadas(prev => [...prev, opcao])
                                                }
                                            }}
                                            style={{
                                                margin: 4,
                                                borderRadius: 15,
                                                backgroundColor: isSelected ? "#f57c00" : "#f5f5f5",
                                                color: isSelected ? "white" : "#666",
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        ))}

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginTop={2}
                    >
                        <Typography
                            variant="body2"
                            style={{ color: "#95a5a6", fontStyle: "italic" }}
                        >
                            {tabValue === 0
                                ? `${produtosFiltrados.length} produtos encontrados`
                                : `${opcoesFiltradas.length} opções encontradas`}
                        </Typography>
                        <Typography variant="body2" style={{ color: "#34495e", fontWeight: 600 }}>
                            {tabValue === 0
                                ? `Selecionados: ${produtosSelecionados.length}`
                                : `Selecionadas: ${opcoesSelecionadas.length}`}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions style={{ padding: 16 }}>
                    <Button
                        onClick={() => setOpenDialogVinculacao(false)}
                        style={{ borderRadius: 15 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={criarFichaTecnica}
                        variant="contained"
                        disabled={
                            produtosSelecionados.length === 0 && opcoesSelecionadas.length === 0
                        }
                        style={{
                            borderRadius: 15,
                            backgroundColor: "var(--cor-botao-salvar)",
                            color: "white",
                        }}
                    >
                        Criar {produtosSelecionados.length + opcoesSelecionadas.length} Ficha(s)
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
